import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { DialogSettingsServiceUsersAdd } from 'app/dialogs/settings/serviceusers/add/dialog-settings-serviceusers-add';
import { DialogSettingsServiceUsersDetails } from 'app/dialogs/settings/serviceusers/details/dialog-settings-serviceusers-details';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListServiceUsersRes } from 'app/interfaces/responses/Exposed/exposedListServiceUsersRes';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListRoleGroupsRes } from 'app/interfaces/responses/List/listRoleGroupsRes';
import { ListRolesRes } from 'app/interfaces/responses/List/listRolesRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { ServiceUsersService } from 'app/services/api/serviceusers/serviceusers.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import latinize from 'latinize';
import { Subscription, startWith } from 'rxjs';

@Component({
    selector: 'app-settings-serviceusers',
    templateUrl: './settings-serviceusers.component.html',
})
export class ServiceusersComponent implements OnInit {
    drawerMode: 'side' | 'over';
    usersCount: number = 0;
    searchInputControl: UntypedFormControl = new UntypedFormControl();
    selectedUser: ExposedListServiceUsersRes;
    searchedUsers: Array<ExposedListServiceUsersRes> = [];
    users: Array<ExposedListServiceUsersRes> = [];
    searchInput: string;
    hasHttpFetched: boolean = false;
    roleGroups: ListRoleGroupsRes[] = [];
    roles: ListRolesRes[] = [];
    userDetails: ExposedReadUserDetailsRes = null;
    domainsList: Array<DropDownItem> = [];
    searchSub?: Subscription;

    constructor(
        public serviceUsersService: ServiceUsersService,
        public lsrauthService: LsrauthService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        public dialog: MatDialog,
        private formBuilder: UntypedFormBuilder,
        private translationService: TranslationService,
        private _fuseConfirmationService: FuseConfirmationService,
        @Inject(DOCUMENT) private _document: any
    ) {}

    ngOnInit(): void {
        this.getServiceUsers();
        this.getRoles();
        this.getDomains();

        this.searchSub = this.searchInputControl.valueChanges.pipe(startWith('')).subscribe(() => this.searchServiceUser());
    }

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
    }

    getServiceUsers() {
        this.serviceUsersService.listServiceUsers().subscribe((res) => {
            this.users = res;
            this.searchedUsers = this.users;
            this.usersCount = this.users.length;
        });
    }

    getDomains() {
        this.serviceUsersService.listServiceUserDomains().subscribe((res) => {
            this.domainsList = res.map((domain) => {
                return {
                    value: domain.domainId,
                    viewValue: domain.domainName,
                } as DropDownItem;
            });
        });
    }

    getRoles() {
        this.lsrauthService.getServiceUsersAccessRoles().then((value) => {
            this.roleGroups = value.roleGroups;
            this.roles = value.roles;
        });
    }

    searchServiceUser() {
        const q = (this.searchInputControl.value ?? '').toString().trim().toLowerCase();
        const users = this.users ?? [];

        if (!q) {
            this.searchedUsers = users;
        } else {
            this.searchedUsers = users
                .filter((u) => (u.username ?? '').toLowerCase().includes(q) || (u.domainName ?? '').toLowerCase().includes(q))
                .sort(this.sortServiceUsers);
        }
        this.usersCount = this.searchedUsers.length;
    }

    trackByFn(_: number, user: ExposedListServiceUsersRes) {
        return user.username;
    }

    sortServiceUsers(a: ExposedListServiceUsersRes, b: ExposedListServiceUsersRes) {
        if (latinize(a.domainName) < latinize(b.domainName)) {
            return -1;
        }
        if (latinize(a.domainName) > latinize(b.domainName)) {
            return 1;
        }
        return 0;
    }

    addServiceUser() {
        const dialogRef = this.dialog.open(DialogSettingsServiceUsersAdd, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                data: this.domainsList,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            this.getServiceUsers();
        });
    }

    viewServiceUserDetails(user: ExposedListServiceUsersRes) {
        const dialogRef = this.dialog.open(DialogSettingsServiceUsersDetails, {
            autoFocus: false,
            restoreFocus: false,
            width: '550px',
            data: {
                user: user,
                roleGroups: this.roleGroups,
                roles: this.roles,
            },
        });

        dialogRef.componentInstance.onConfirm.subscribe((emitValue) => {
            if (emitValue) {
                this.getServiceUsers();
            }
        });

        dialogRef.afterClosed().subscribe((result) => {});
    }

    DeleteServiceUser(user) {
        let confirmationForm = this.formBuilder.group({
            title: 'Eyða kerfisnotanda',
            message: this.translationService.translate('info_confirm_delete_user_message'),
            icon: {
                show: true,
                name: 'heroicons_solid:trash',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: this.translationService.translate('label_confirm'),
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: this.translationService.translate('label_cancel'),
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(confirmationForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.serviceUsersService
                    .deleteServiceUser({
                        userId: user.userId,
                        username: user.username,
                    })
                    .subscribe((result) => {
                        this.getServiceUsers();
                    });
            }
        });
    }

    /**
     * On backdrop clicked
     */
    onBackdropClicked(): void {
        // Go back to the list
        this._router.navigate(['./'], { relativeTo: this._activatedRoute });
    }
}
